import React, { useMemo } from "react";
import { faGlobe } from "@fortawesome/free-solid-svg-icons";
import type { DevToolsPlugin } from "../../types";
import { Accordion } from "../../Accordion";

interface Site {
  name: string;
  description: string;
  href: string;
}

interface SitesPluginProps {
  mainSites: Site[];
  ukSites: Site[];
  usSites: Site[];
  verticals: Record<string, string>;
}

const sortAlphabetically = (sites: Site[]) =>
  [...sites].sort((a, b) => a.description.localeCompare(b.description));

function SitesPanel({ mainSites, ukSites, usSites, verticals }: SitesPluginProps) {
  const verticalEntries = Object.entries(verticals);
  const sortedUkSites = useMemo(() => sortAlphabetically(ukSites), [ukSites]);
  const sortedUsSites = useMemo(() => sortAlphabetically(usSites), [usSites]);

  return (
    <>
      <Accordion title="Main Sites" count={mainSites.length}>
        <div className="dt-chip-grid">
          {mainSites.map((site) => (
            <a key={site.name} className="dt-chip" href={site.href}>{site.description}</a>
          ))}
        </div>
      </Accordion>

      {sortedUkSites.length > 0 && (
        <Accordion title="UK Partners" count={sortedUkSites.length}>
          <div className="dt-chip-grid">
            {sortedUkSites.map((site) => (
              <a key={site.name} className="dt-chip" href={site.href}>{site.description}</a>
            ))}
          </div>
        </Accordion>
      )}

      {sortedUsSites.length > 0 && (
        <Accordion title="US Partners" count={sortedUsSites.length}>
          <div className="dt-chip-grid">
            {sortedUsSites.map((site) => (
              <a key={site.name} className="dt-chip" href={site.href}>{site.description}</a>
            ))}
          </div>
        </Accordion>
      )}

      <Accordion title="Verticals" count={verticalEntries.length}>
        <div className="dt-chip-grid">
          {verticalEntries.map(([name, href]) => (
            <a key={name} className="dt-chip" href={href}>{name}</a>
          ))}
        </div>
      </Accordion>
    </>
  );
}

export function sitesPlugin(props: SitesPluginProps): DevToolsPlugin {
  return {
    id: "sites",
    name: "Sites & Verticals",
    icon: faGlobe,
    render: () => <SitesPanel {...props} />,
  };
}
